"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.instagramScraper = exports.instagramScraperArgsSchema = exports.InstagramScraperAction = exports.instagramScrapeFollowingResultSchema = exports.instagramScrapeFollowingArgsSchema = exports.instagramScrapeFollowersResultSchema = exports.instagramScrapeFollowersArgsSchema = exports.instagramScrapeProfileResultSchema = exports.instagramScrapeProfileArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// Schema for individual Instagram profile data
var instagramProfileSchema = zod_1.z
    .object({
    id: zod_1.z.number().describe('Internal database ID'),
    bio: zod_1.z.string().nullish().describe('Profile biography'),
    pfp: zod_1.z.string().nullish().describe('Profile picture URL'),
    user: zod_1.z.number().describe('User ID'),
    email: zod_1.z
        .string()
        .nullish()
        .describe('Email address extracted from bio (up to 10 emails per profile)'),
    phone: zod_1.z.string().nullish().describe('Phone number extracted from bio or profile'),
    posts: zod_1.z.number().nullish().describe('Post count'),
    result: zod_1.z.number().nullish().describe('Result ID'),
    website: zod_1.z.string().nullish().describe('Website URL from profile'),
    category: zod_1.z.string().nullish().describe('Business category if applicable'),
    location: zod_1.z.string().nullish().describe('Location from profile'),
    username: zod_1.z.string().describe('Instagram username'),
    followers: zod_1.z.number().nullish().describe('Follower count'),
    following: zod_1.z.number().nullish().describe('Following count'),
    full_name: zod_1.z.string().nullish().describe('Full name from profile'),
    last_name: zod_1.z.string().nullish().describe('Last name extracted from full name'),
    first_name: zod_1.z.string().nullish().describe('First name extracted from full name'),
    profile_link: zod_1.z.string().nullish().describe('Instagram profile URL'),
    scraped_from: zod_1.z.string().nullish().describe('Source account this was scraped from'),
    email_type: zod_1.z.string().nullish().describe('Type of email extracted'),
    external_url: zod_1.z.string().nullish().describe('External URL if available'),
    is_business: zod_1.z.boolean().nullish().describe('Whether this is a business account'),
    is_verified: zod_1.z.boolean().nullish().describe('Whether this account is verified'),
    is_private: zod_1.z.boolean().nullish().describe('Whether this account is private'),
})
    .passthrough();
// ---------- Scrape Profile ----------
exports.instagramScrapeProfileArgsSchema = zod_1.z
    .object({
    username: zod_1.z
        .string()
        .describe('The Instagram username to scrape for email addresses and contact info'),
    emailLimit: zod_1.z
        .number()
        .nullish()
        .describe('Maximum number of emails to extract from profile bio (defaults to 100)'),
})
    .passthrough();
exports.instagramScrapeProfileResultSchema = zod_1.z.object({
    profiles: zod_1.z
        .array(instagramProfileSchema)
        .describe('Array containing the scraped account profile WITH extracted email addresses'),
});
// ---------- Scrape Followers ----------
exports.instagramScrapeFollowersArgsSchema = zod_1.z
    .object({
    username: zod_1.z
        .string()
        .describe('The Instagram username to scrape followers from (extracts email addresses from each follower)'),
    emailLimit: zod_1.z
        .number()
        .nullish()
        .describe('Maximum number of emails to extract from EACH follower bio for lead generation (defaults to 100)'),
})
    .passthrough();
exports.instagramScrapeFollowersResultSchema = zod_1.z.object({
    profiles: zod_1.z
        .array(instagramProfileSchema)
        .describe('Array of follower profiles WITH extracted email addresses for contact building'),
    scraped_from: zod_1.z.string().nullish().describe('Username that followers were scraped from'),
    total_scraped: zod_1.z.number().nullish().describe('Total number of followers scraped'),
});
// ---------- Scrape Following ----------
exports.instagramScrapeFollowingArgsSchema = zod_1.z
    .object({
    username: zod_1.z
        .string()
        .describe('The Instagram username to scrape following list from (extracts email addresses from each account)'),
    emailLimit: zod_1.z
        .number()
        .nullish()
        .describe('Maximum number of emails to extract from EACH following account bio for contact building (defaults to 100)'),
})
    .passthrough();
exports.instagramScrapeFollowingResultSchema = zod_1.z.object({
    profiles: zod_1.z
        .array(instagramProfileSchema)
        .describe('Array of following profiles WITH extracted email addresses for outreach'),
    scraped_from: zod_1.z.string().nullish().describe('Username that following list was scraped from'),
    total_scraped: zod_1.z.number().nullish().describe('Total number of following accounts scraped'),
});
// ==========================
// Compound Tool: instagramScraper
// ==========================
exports.InstagramScraperAction = zod_1.z.enum([
    'scrapeProfile',
    'scrapeFollowers',
    'scrapeFollowing',
]);
exports.instagramScraperArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({ name: zod_1.z.literal('scrapeProfile'), params: exports.instagramScrapeProfileArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('scrapeFollowers'), params: exports.instagramScrapeFollowersArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('scrapeFollowing'), params: exports.instagramScrapeFollowingArgsSchema }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var InstagramScraperAllResultsSchema = zod_1.z.union([
    exports.instagramScrapeProfileResultSchema,
    exports.instagramScrapeFollowersResultSchema,
    exports.instagramScrapeFollowingResultSchema,
]);
var instagramScraperResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'scrapeProfile' }, schema: exports.instagramScrapeProfileResultSchema },
        { when: { 'action.name': 'scrapeFollowers' }, schema: exports.instagramScrapeFollowersResultSchema },
        { when: { 'action.name': 'scrapeFollowing' }, schema: exports.instagramScrapeFollowingResultSchema },
    ],
    defaultSchema: InstagramScraperAllResultsSchema,
});
exports.instagramScraper = (0, common_1.createToolMetadata)({
    id: 'instagramScraper',
    displayName: 'Instagram Scraper',
    displayDescription: 'Extract email addresses and contact info from Instagram profiles, followers, and following lists',
    category: 'search',
    color: common_1.BRAND_COLORS.INSTAGRAM,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.INSTAGRAM),
    icon: 'default',
    argsSchema: exports.instagramScraperArgsSchema,
    adminOnly: false,
}, instagramScraperResultSchemas);
